import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonModal
} from "@ionic/react";
import { SetupBot } from "./SetupBot";
import { useState } from "react";
import { chevronBack } from "ionicons/icons";
import UserName from "../components/UserName";
import AnimationContainer from "../components/Animation";
import { getTimeRemaining } from "../components/LobbyInfo";
import ConfirmationPage from "./Confirmation";
import { useUsersStore } from "../stores/users.store";
import { useBotsStore } from "../stores/bots.store";
import { Bot } from "../types/bot.type";
import { useAuthStore } from "../stores/auth.store";
import axios from "axios";
import FavouriteBots from "../components/FavouriteBots";

interface JoinLobbyProps {
  onDismiss: () => void;
  lobby: any;
  navigateTo?: (path: string) => void;
  title?: string;
}

function formatLongDate(date: Date) {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
  ];

  const dayName = days[date.getDay()];
  const day = date.getDate();
  const monthName = months[date.getMonth()];
  const year = `'${date.getFullYear().toString().substr(-2)}`; // Get the last two digits of the year

  return `${dayName}, ${day} ${monthName} ${year}`;
}

const JoinLobby: React.FC<JoinLobbyProps> = ({
  onDismiss,
  navigateTo,
  lobby,
  title
}) => {
  const uid = useAuthStore(state => state.user?.uid);
  const [activePage, setActivePage] = useState(0);
  const [selectedBots, setSelectedBots] = useState<string[]>([]);
  const [createdBot, setCreatedBot] = useState<any>(null);
  const favouriteBots: any[] = useUsersStore(state =>
    state?.userFavourites?.sort((a: any, b: any) =>
      a.profit > b.profit ? 1 : -1
    )
  );
  const favouriteBotIds = favouriteBots?.map((strat: any) => strat.botId);
  const bots = useBotsStore(state =>
    state.bots?.filter((bot: any) => favouriteBotIds?.includes(bot.id))
  );
  const [presentSetupBot, dismissSetupBot] = useIonModal(SetupBot, {
    lobbyId: lobby?.id,
    mode: "simulation",
    onDismiss: (bot: any) => {
      dismissSetupBot();
      if (bot) {
        setCreatedBot(bot);
        setSelectedBots(["toBeCreated"]);
        presentConfirm();
      }
    }
  });

  const [presentConfirm, dismissConfirm] = useIonModal(ConfirmationPage, {
    onDismiss: (confirm: boolean) => {
      if (confirm) {
        enterBotsIntoLobby();
      }
      dismissConfirm();
    },
    title: "Confirm Transaction",
    confirmText: "Confirm Transaction",
    contentHtml: `
      <div class="flex flex-col ion-text-center px-16 py-4 text-primary-30">
        <div class="pb-4 mx-auto">You are entering ${selectedBots?.length} Bot${
      selectedBots?.length !== 1 ? "s" : " "
    } into the ${lobby?.name} lobby.</div>
        <div class="mx-auto">Please note: <b>${
          selectedBots?.length * lobby?.buyIn
        } QUO</b> will be deducted from your wallet</div>
      </div>
    `,
    cssClass: "transparent-modal"
  });

  const onSelect = (bot: any) => {
    const botId = bot?.id;
    if (selectedBots.includes(botId)) {
      setSelectedBots(prevBots => prevBots.filter(id => id !== botId));
    } else {
      setSelectedBots(prevBots => [...prevBots, botId]);
    }
  };

  const enterBotsIntoLobby = async () => {
    let botsToEnter = [];
    if (createdBot) {
      botsToEnter.push(createdBot);
    } else {
      const selectedBotRefs = bots.filter((bot: Bot) =>
        selectedBots.includes(bot.id)
      );
      botsToEnter = selectedBotRefs.map((bot: Bot) => ({
        exchange: bot?.exchange,
        lockSettings: lobby?.lockedBots,
        name: bot?.name,
        notifications: bot?.notifications,
        orderType: "market",
        private: false,
        quoteAsset: bot?.quoteAsset,
        startAsset: bot?.startAsset,
        strategyType: bot?.strategyType,
        tradeParameters: bot?.tradeParameters,
        version: "2.0",
        weightedCoins: bot?.weightedCoins,
        whitelist: bot?.whitelist,
        img: bot?.img,
        mode: "simulation",
        user: uid,
        provisioned: false,
        lobby: lobby?.id
      }));
    }

    await Promise.all(
      botsToEnter.map((bot: any) =>
        axios.post(
          "https://us-central1-quidpro-903c6.cloudfunctions.net/enterBotIntoLobby",
          { lobbyId: lobby?.id, bot }
        )
      )
    );
    navigateTo?.(`/lobbies/${lobby?.id}`);
    onDismiss();
  };

  // useEffect(() => {
  //   if (activePage === 1) {
  //     let unsubscribes: any[] = [];
  //     if (favouriteBots?.length > 0) {
  //       unsubscribes = favouriteBots.map((strat: any) =>
  //         subscribeToBot(strat.botId)
  //       );
  //     }
  //     return () => {
  //       unsubscribes.forEach((unsubscribe: any) => unsubscribe());
  //     };
  //   }

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [activePage]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          {activePage > 0 && (
            <IonButtons slot="start">
              <IonButton
                onClick={() => {
                  setActivePage(activePage - 1);
                }}
              >
                <IonIcon className="text-white" icon={chevronBack} />
              </IonButton>
            </IonButtons>
          )}
          <IonTitle>
            {title || activePage === 1 ? "My Saved Bots" : "Join Lobby"}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="h-full w-full flex justify-center bg-primary-90">
          {activePage === 0 && (
            <div className="ion-text-center w-full px-8">
              <div className="px-4 pt-2 text-2xl text-white">{lobby?.name}</div>
              <div className="pb-4 flex justify-center">
                <span className="pr-2 text-sm text-slate-500">Hosted by</span>
                <UserName userId={lobby?.administrators[0]} />
              </div>
              <AnimationContainer animationName="trophyAnimation" />
              <div className="text-white">
                <div className="flex justify-between py-1">
                  <div className="text-left">Buy In (Per Bot)</div>
                  <div className="text-right">
                    <b>{lobby?.buyIn}</b> QUO
                  </div>
                </div>
                <div className="flex justify-between py-1">
                  <div className="text-left">No. Bots</div>
                  <div className="text-right">
                    <b>{lobby?.bots?.length || 0}</b>
                  </div>
                </div>
                <div className="flex justify-between py-1">
                  <div className="text-left">Total Pot (current)</div>
                  <div className="text-right">
                    <b>{lobby?.totalPot}</b> QUO
                  </div>
                </div>
                <div className="flex justify-between py-4">
                  <div className="text-left">Starts In</div>
                  <div className="text-right">
                    {getTimeRemaining(new Date(lobby?.startDate))}
                  </div>
                </div>
                <div className="flex justify-between py-1">
                  <div className="text-left">Start Date</div>
                  <div className="text-right">
                    {formatLongDate(new Date(lobby?.startDate))}
                  </div>
                </div>
                <div className="flex justify-between py-1 pb-2">
                  <div className="text-left">End Date</div>
                  <div className="text-right">
                    {formatLongDate(new Date(lobby?.endDate))}
                  </div>
                </div>
              </div>
              <div className="flex flex-col w-5/6 fixed bottom-0">
                <IonButton
                  expand="block"
                  className="h-14"
                  color="tertiary"
                  disabled={!favouriteBotIds || favouriteBotIds?.length === 0}
                  onClick={() => setActivePage(1)}
                >
                  Enter Existing Bots
                </IonButton>
                <IonButton
                  expand="block"
                  fill="outline"
                  color="tertiary"
                  className="h-14"
                  onClick={() => presentSetupBot()}
                >
                  Enter a New Bot
                </IonButton>

                <IonButton
                  fill="clear"
                  expand="block"
                  onClick={() => onDismiss()}
                >
                  <span className="text-primary-50 text-sm">Cancel</span>
                </IonButton>
              </div>
            </div>
          )}
          {activePage === 1 && (
            <div className="h-full w-full">
              <div className="flex flex-row justify-between py-4">
                <span className="text-2xl font-semibold pl-4">Buy-in</span>
                <div className="flex justify-center items-center w-fit font-semibold text-xl py-1 px-2 rounded bg-primary-80 text-primary-30 mr-8">
                  {selectedBots?.length * lobby?.buyIn} QUO
                </div>
              </div>
              <div className="bg-primary-90 p-2">
                <div className="flex ml-auto pr-14 w-min gap-x-4">
                  {["Trades", "Streak", "Profit"].map(key => (
                    <div
                      key={key}
                      className="text-[10px] text-primary-40 uppercase"
                    >
                      {key}
                    </div>
                  ))}
                </div>
              </div>
              <FavouriteBots
                onSelect={onSelect}
                selectedBots={selectedBots}
                multiples
              />
              <div className="flex flex-col w-full fixed bottom-0 px-4">
                <IonButton
                  expand="block"
                  className="h-14"
                  disabled={selectedBots?.length === 0}
                  color="tertiary"
                  onClick={() => presentConfirm()}
                >
                  Enter Bots into Lobby
                </IonButton>

                <IonButton
                  fill="clear"
                  expand="block"
                  onClick={() => onDismiss()}
                >
                  <span className="text-primary-50 text-sm">Cancel</span>
                </IonButton>
              </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default JoinLobby;
